import { GroupInterface, LocationInterface, LocationPageInterface, LocationVarKey, OrgInterface } from "@swivl-mono/types";
import { Spinner } from "@swivl-mono/ui";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaChevronRight } from "react-icons/fa6";
import { Location, useLocation, useNavigate } from "react-router-dom";

import { SidebarLink } from "../../../Components/SidebarLink/SidebarLink";
import { buildCRMSearchParams } from "../../../Helpers/CRM/searchParams.Helper";
import { FacilityVarSet, facilityVarKeysFor } from "../../../Helpers/FeatureFlags/facilityVarKeys";
import { useLocationVariables } from "../../../Hooks/useLocationVariables";
import { useUrlState } from "../../../Hooks/useUrlState";
import LocationsModel from "../../../Models/Locations/Locations.Model";
import { Member } from "../../../Models/Member/Member.Current";
import "./LocationListItemContent.scss";

interface LocationListItemProps {
  /** Which facility-variable set the RENDERING PAGE lives on (ENG-3198). */
  varSet: FacilityVarSet;
  org?: OrgInterface;
  group?: GroupInterface;
  location?: LocationInterface;
}

function LocationListItemContent(props: LocationListItemProps) {
  const { org, group, location, varSet } = props;
  // ENG-3198: this sidebar renders on legacy PM/CRM pages AND Properties v3 —
  // the page passes which variable set it lives on; never Cortex-only.
  const facilityKeys = facilityVarKeysFor(varSet);
  const vars = useLocationVariables([facilityKeys.name, facilityKeys.imageURL], group?.id, location?.id);

  let name, subtitle, avatarURL, showAvatar, showChevron, supressTextHover;

  if (org) {
    supressTextHover = true;
    name = vars?.[facilityKeys.name];
    subtitle = "Organization";
    avatarURL = vars?.[facilityKeys.imageURL];
    showAvatar = true;
    showChevron = true;
  }
  if (group) {
    name = vars?.[facilityKeys.name];
    avatarURL = vars?.[facilityKeys.imageURL];
    showAvatar = true;
    showChevron = true;
    supressTextHover = true;
  }
  if (location) {
    name = vars?.[facilityKeys.name];
    showChevron = false;
  }

  if (!location && !group && subtitle !== "Organization") {
    name = "No Location";
    showChevron = false;
  }

  return (
    <div className={`LocationListItemContent flex h-full w-full flex-row items-center justify-start ${supressTextHover ? "supressTextHover" : ""}`}>
      {showAvatar ? (
        <div className="bg-grey avatarSpacer mr-3 h-8 w-8">
          {avatarURL && <img className="h-8 w-8 rounded-xs bg-white object-cover" src={avatarURL} alt={name + " logo"} />}
        </div>
      ) : (
        <div className="blackSpace mr-3 h-8 w-8" />
      )}
      <div className="nameAndSubtitle flex flex-1 flex-col items-start justify-center">
        <div className="name leading-none">{name}</div>
        {subtitle && <div className="subtitle text-xs italic">{subtitle}</div>}
      </div>
      {showChevron && (
        <div className="chevron">
          <FaChevronRight />
        </div>
      )}
    </div>
  );
}

interface LocationsListComponentProps extends LocationPageInterface {
  /**
   * Which facility-variable set the page embedding this list lives on
   * (ENG-3198): Properties v3 passes "cortex"; legacy PM/CRM callers omit it
   * and read the legacy keys their worksheets maintain.
   */
  varSet?: FacilityVarSet;
  searchQuery?: string;
  prependLinks?: { name: string; url: string | { pathname?: string; search?: string; hash?: string } }[];
}

type NavItemUrl = string | { pathname?: string; search?: string; hash?: string };

type NavItem =
  | { type: "org"; url: NavItemUrl; excludeMatch: string }
  | { type: "group"; url: NavItemUrl; excludeMatch: string; groupId: number }
  | { type: "location"; url: NavItemUrl; groupId: number; locationId: number };

function navItemMatchesLocation(item: NavItem, routerLocation: Location): boolean {
  const { url } = item;
  const excludeMatch = "excludeMatch" in item ? item.excludeMatch : undefined;
  let matches = false;

  if (typeof url === "string") {
    const escapedTo = url.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    matches = new RegExp(`(^|/)${escapedTo}(/|$)`).test(routerLocation.pathname);
  } else if (typeof url === "object" && "search" in url && routerLocation.search) {
    const toParams = new URLSearchParams(url.search);
    const locationParams = new URLSearchParams(routerLocation.search);
    matches = [...toParams.entries()].every(([k, v]) => locationParams.get(k) === v);
    if (matches) {
      const linkHasGroupId = toParams.has("groupId");
      const linkHasLocationId = toParams.has("locationId");
      if (linkHasGroupId && !linkHasLocationId && locationParams.has("locationId")) {
        matches = false;
      }
    }
  }

  if (matches && excludeMatch && routerLocation.pathname.includes(excludeMatch)) {
    matches = false;
  }

  return matches;
}

export function LocationsListComponent(props: LocationsListComponentProps) {
  const varSet: FacilityVarSet = props.varSet ?? "legacy";
  const searchQuery = props.searchQuery ?? "";
  const { groups } = LocationsModel.useState();
  const org: OrgInterface = Member.org!;
  const page = props.page ? props.page : "details";
  const action = props.action ? "/" + props.action : "";
  const subpage = props.subPage ? "/" + props.subPage : "";
  // let pageUrl = props.page ? '/page/' + page : '/page/details' + action;
  // if (props.subPage && page) { pageUrl += '/' + props.subPage; }
  const pageUrl = "page/" + page + subpage + action;
  const { searchParams } = useUrlState();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const listRef = useRef<HTMLDivElement>(null);

  // filter groups and locations based on member's location permissions
  const permissionFilteredGroups = useMemo(() => {
    if (!groups || !Member.member?.permissions) return groups;

    // get the location permissions from the member's first permission that has locations defined
    const locationPermissions = Member.member.permissions.map((p) => p.locations).filter((loc) => loc);

    // check if member has org-wide access
    const hasOrgWideAccess = locationPermissions.some((loc) => loc.orgId === Member.orgId);

    if (hasOrgWideAccess) {
      return groups;
    }

    // get all allowed groupids and locationids
    const allowedGroupIds: number[] = [];
    const allowedLocationIds: number[] = [];

    locationPermissions.forEach((loc) => {
      loc.groupIds?.forEach((id) => {
        if (!allowedGroupIds.includes(id)) allowedGroupIds.push(id);
      });
      loc.locationIds?.forEach((id) => {
        if (!allowedLocationIds.includes(id)) allowedLocationIds.push(id);
      });
    });

    // filter groups and locations
    return groups
      .map((group) => {
        const isGroupAllowed = allowedGroupIds.includes(group.id);

        // if entire group is allowed, include all locations
        if (isGroupAllowed) {
          return group;
        }

        // only include specific locations that are allowed
        const filteredLocations = group.locations?.filter((loc) => allowedLocationIds.includes(loc.id)) ?? [];

        // only include the group if it has allowed locations
        if (filteredLocations.length > 0) {
          return {
            ...group,
            locations: filteredLocations
          };
        }

        return null;
      })
      .filter((group) => group !== null) as GroupInterface[];
  }, [groups]);

  const filteredGroups = useMemo(() => {
    if (!permissionFilteredGroups) return null;
    const sortedGroups = permissionFilteredGroups.map((group) => ({
      ...group,
      locations: [...(group?.locations ?? [])].sort((a, b) => a.name.localeCompare(b.name))
    }));

    if (!searchQuery) {
      return sortedGroups;
    }

    const searchLower = searchQuery.toLowerCase();

    return sortedGroups
      .map((group) => {
        const isGroupMatch = group.name.toLowerCase().includes(searchLower);

        const matchingLocations =
          group.locations?.filter((loc) => {
            return loc.name.toLowerCase().includes(searchLower);
          }) ?? [];

        if (isGroupMatch) {
          return {
            ...group,
            locations: group.locations ?? []
          };
        }

        if (matchingLocations.length > 0) {
          return {
            ...group,
            locations: matchingLocations
          };
        }

        return null;
      })
      ?.filter((group) => group !== null) as GroupInterface[];
  }, [permissionFilteredGroups, searchQuery]);

  // Derive the route root from the current pathname so the sidebar links
  // stay inside the surface the user navigated from. Hardcoding `/properties`
  // sent v3 users back to v2 when they clicked a location.
  // The CRM surface (and its sub-pages, e.g. /crm/sequence-suppressions) navigates the
  // sidebar via search params on the current pathname, not via path segments.
  const isCRM = !!props?.path?.startsWith("/crm");
  const propertiesRoot = props?.path?.startsWith("/properties-v3") ? "/properties-v3" : "/properties";

  const orgUrl = isCRM ? { search: buildCRMSearchParams({ level: "org" }, searchParams) } : `${propertiesRoot}/${pageUrl}`;

  // Single source of truth for all navigable items: URL, type, IDs, and excludeMatch
  const navItems = useMemo<NavItem[]>(() => {
    const items: NavItem[] = [];
    if (Member.canSeeOrgSettings) {
      items.push({ type: "org", url: orgUrl, excludeMatch: "/group" });
    }
    if (filteredGroups) {
      for (const group of filteredGroups) {
        const groupUrl = isCRM
          ? { search: buildCRMSearchParams({ level: "group", groupId: group.id }, searchParams) }
          : `${propertiesRoot}/group/${group.id}/${pageUrl}`;
        items.push({ type: "group", url: groupUrl, excludeMatch: "/location", groupId: group.id });
        for (const loc of group.locations ?? []) {
          const locationUrl = isCRM
            ? { search: buildCRMSearchParams({ level: "location", groupId: group.id, locationId: loc.id }, searchParams) }
            : `${propertiesRoot}/group/${group.id}/location/${loc.id}/${pageUrl}`;
          items.push({ type: "location", url: locationUrl, groupId: group.id, locationId: loc.id });
        }
      }
    }
    return items;
  }, [filteredGroups, orgUrl, props?.path, searchParams, pageUrl]);

  // Derived lookup maps for rendering — no separate URL computation needed in JSX
  const { groupUrlMap, locationUrlMap, navIndexMap } = useMemo(() => {
    const groupUrlMap: Record<number, NavItemUrl> = {};
    const locationUrlMap: Record<number, NavItemUrl> = {};
    const navIndexMap: { org: number | undefined; groups: Record<number, number>; locations: Record<number, number> } = {
      org: undefined,
      groups: {},
      locations: {}
    };
    navItems.forEach((item, idx) => {
      if (item.type === "org") navIndexMap.org = idx;
      if (item.type === "group") {
        groupUrlMap[item.groupId] = item.url;
        navIndexMap.groups[item.groupId] = idx;
      }
      if (item.type === "location") {
        locationUrlMap[item.locationId] = item.url;
        navIndexMap.locations[item.locationId] = idx;
      }
    });
    return { groupUrlMap, locationUrlMap, navIndexMap };
  }, [navItems]);

  // Sync focusedIndex with the current route so arrow keys start from the selected item
  useEffect(() => {
    const idx = navItems.findIndex((item) => navItemMatchesLocation(item, routerLocation));
    setFocusedIndex(idx);
  }, [navItems, routerLocation]);

  // Auto-focus list on mount so arrow keys work immediately without a mouse click
  useEffect(() => {
    listRef.current?.focus();
  }, []);

  // Scroll focused item into view
  useEffect(() => {
    if (focusedIndex < 0 || !listRef.current) return;
    listRef.current.querySelector<HTMLElement>(`[data-nav-index="${focusedIndex}"]`)?.scrollIntoView({ block: "nearest" });
  }, [focusedIndex]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent): void => {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const nextIndex = e.key === "ArrowDown" ? Math.min(focusedIndex + 1, navItems.length - 1) : Math.max(focusedIndex - 1, 0);
        const url = navItems[nextIndex]?.url;
        if (url) {
          setFocusedIndex(nextIndex);
          navigate(url);
        }
      }
    },
    [focusedIndex, navItems, navigate]
  );

  return (
    <div ref={listRef} tabIndex={0} onKeyDown={handleKeyDown} className="outline-none">
      {Member.canSeeOrgSettings && (
        <SidebarLink greyBackground={true} borderBottom={true} to={orgUrl} excludeMatch={`/group`} height={55} navIndex={navIndexMap.org}>
          <LocationListItemContent org={org} varSet={varSet} />
        </SidebarLink>
      )}
      {props?.prependLinks &&
        props.prependLinks.map((link) => (
          <SidebarLink key={"prependlink_" + link.url} to={link.url} height={38}>
            <div className="flex h-full w-full flex-row items-center justify-start pl-11">{link.name}</div>
          </SidebarLink>
        ))}
      {!filteredGroups ? (
        <div className="flex w-full items-center justify-center p-10">
          {" "}
          <Spinner />
        </div>
      ) : (
        filteredGroups?.map((group) => (
          <div key={"groupgroup_" + group.id}>
            <SidebarLink
              greyBackground={true}
              key={"group_" + group.id}
              to={groupUrlMap[group.id]}
              excludeMatch={`/location`}
              height={38}
              navIndex={navIndexMap.groups[group.id]}
            >
              <LocationListItemContent group={group} varSet={varSet} />
            </SidebarLink>
            {group?.locations?.map((loc) => (
              <SidebarLink key={group.id + "_loc_" + loc.id} to={locationUrlMap[loc.id]} height={38} navIndex={navIndexMap.locations[loc.id]}>
                <LocationListItemContent location={loc} varSet={varSet} />
              </SidebarLink>
            ))}
          </div>
        ))
      )}
    </div>
  );
}
